import { omit } from 'lodash';
import moment, { Moment } from 'moment';
import CleaningStatus from '@/dossier/constants/CleaningStatus';
import PickingStatus from '@/dossier/constants/PickingStatus';
import VehicleStatus from '@/dossier/constants/VehicleStatus';
import WorkStatus, { READY_WORK_STATUSES } from '@/dossier/constants/WorkStatus';
import { DossierId, DossierReference } from '@/dossier/models/AbstractDossier';
import { WorkshopTeamId } from '@/general/models/WorkshopTeam';
import QualityControlStatus from '@/shared/constants/QualityControlStatus';
import Tag from '@/shared/models/Tag';
import { UserId } from '@/shared/models/User';
import { durationAsMinutes, formatDuration } from '@/shared/utils/dateTime';
import { isPresent, isTruthy, present } from '@/shared/utils/typescript';
import Vehicle from '@/vehicle/models/Vehicle';
import WorkPreparationStatus from '@/workPreparation/constants/WorkPreparationStatus';
import PlannerDossierCategory from '../constants/PlannerDossierCategory';
import PlannerEventType from '../constants/PlannerEventType';
import { DEFAULT_PLANNER_EVENT_DURATION } from '../constants/general';
import DossierDetails from './DossierDetails';
import PlannerDossier from './PlannerDossier';
import { WorkshopPlanningUserSettings } from './WorkshopPlanningUserSettings';
import { PlannerEventId, PlannerResourceId } from './ids';

/**
 * A unique value that is assigned to planner events in the front-end (store).
 * This provides a reliable way to reference events since they don't always have IDs in the back-end yet.
 */
export type PlannerEventKey = string;

let eventKeySequence = 0;

export default class PlannerEvent {
  id: PlannerEventId | null = null;
  key: PlannerEventKey = `${++eventKeySequence}`;
  resourceId: PlannerResourceId | null = null;
  start: Moment | null = null;
  end: Moment | null = null;
  duration = '';
  editable = false;
  eventType = PlannerEventType.NONPLANNED;
  dossierId?: DossierId;
  hasRepairOrder = false;
  externalReference: DossierReference | null = null;
  isStopped = false;
  vehicleStatus: VehicleStatus | null = null;
  workStatus: WorkStatus | null = null;
  waitsOnPremise = false;
  mobilitySolution = '';
  pickUpAt: Moment | null = null;
  pickingStatus?: PickingStatus;
  checkedInAt: Moment | null = null;
  contacts: Array<string> = [];
  items: Array<string> = [];
  tags: Array<Tag> = [];
  teamId: WorkshopTeamId | null = null;
  isMissingConfirmation = false;
  isCheckedOut = false;
  cleaningStatus: CleaningStatus | null = null;
  eventBy: UserId | null = null;
  dossierOwner: UserId | null = null;
  isMechanicPaused = false;
  isInvoiced = false;
  qualityControlStatus: QualityControlStatus | null = null;
  workPreparationStatus: WorkPreparationStatus | null = null;
  vehicle: Vehicle | null = null;

  static create(resourceId: PlannerResourceId, start: Moment, end: Moment): PlannerEvent {
    const event = new PlannerEvent();
    event.resourceId = resourceId;
    event.start = start;
    event.end = end;
    return event;
  }

  static dummy(dossierDetails: DossierDetails): PlannerEvent {
    const event = new PlannerEvent();
    event.dossierId = present(dossierDetails.id);
    event.duration = dossierDetails.duration ?? DEFAULT_PLANNER_EVENT_DURATION;
    event.pickUpAt = dossierDetails.preferredPickUp;
    event.checkedInAt = dossierDetails.checkedInAt;
    event.vehicle = dossierDetails.object;
    return event;
  }

  /**
   * Used when creating a new event to be able to show and navigate to a preliminary event while
   * waiting for the real one to get loaded via either client call or push message.
   */
  static createPlaceholder(event?: PlannerEvent, eventId?: PlannerEventId, dossier?: PlannerDossier, dossierId?: DossierId): PlannerEvent {
    const result = new PlannerEvent();
    result.id = eventId ?? null;
    result.dossierId = dossierId;
    result.resourceId = event?.resourceId ?? null;
    result.start = event?.start ?? null;
    result.end = event?.end ?? null;
    if (event) result.eventType = PlannerEventType.PLANNED;
    result.teamId = dossier?.team?.id ?? null;
    result.vehicle = dossier?.vehicle ?? null;
    result.mobilitySolution = dossier?.mobilitySolution?.description || '';
    result.waitsOnPremise = dossier?.waitsOnPremise ?? false;
    result.items = dossier?.bookingItems.map(i => i.description) ?? [];
    result.tags = dossier?.dossierTags ?? [];
    return result;
  }

  getDossierCategory(now: Moment): PlannerDossierCategory | undefined {
    if (this.vehicleStatus === VehicleStatus.EXPECTED) return PlannerDossierCategory.EXPECTED;
    if (this.vehicleStatus === VehicleStatus.CHECKED_IN) {
      if (this.workStatus === WorkStatus.NOT_STARTED) {
        if (!this.checkedInAt || this.checkedInAt.isBefore(now, 'day')) return PlannerDossierCategory.EARLIER;
        if (this.checkedInAt.isSame(now, 'day') || this.start?.isSame(now, 'day')) return PlannerDossierCategory.TODAY;
      }
      if (this.workStatus === WorkStatus.BEING_HANDLED || this.workStatus === WorkStatus.ON_HOLD) return PlannerDossierCategory.WORK_STARTED;
    }
  }

  isFiltered(settings: WorkshopPlanningUserSettings): boolean {
    if (this.vehicleStatus !== null && !settings.includedVehicleStatuses.includes(this.vehicleStatus)) return true;
    return this.workStatus !== null && !settings.includedWorkStatuses.includes(this.workStatus);
  }

  matchesQuery(query: string): boolean {
    return [this.externalReference, this.vehicle?.registrationNumber].filter(isTruthy)
      .some(s => s.toLowerCase().startsWith(query));
  }

  get contactsString(): string {
    return this.contacts.join(', ');
  }

  get isInternal(): boolean {
    return this.eventType === PlannerEventType.INTERNAL;
  }

  get isAbsence(): boolean {
    return this.eventType === PlannerEventType.ABSENCE;
  }

  get isLocked(): boolean {
    return !!this.start && !!this.end && !this.editable;
  }

  get showWaitsOnPremise(): boolean {
    return this.waitsOnPremise && !this.isCheckedOut;
  }

  get date(): Moment | null {
    return this.start?.clone().startOf('day') ?? null;
  }

  get plannedDuration(): string {
    if (this.start && this.end) {
      return formatDuration(moment.duration(this.end.diff(this.start)));
    } else if (this.duration) {
      return this.duration;
    } else {
      return '';
    }
  }

  reschedule(to: PlannerEvent): void {
    this.resourceId = to.resourceId;
    this.start = to.start;
    this.end = to.end;
  }

  isObsolete(): boolean {
    return this.eventType === PlannerEventType.NONPLANNED;
  }

  canBeClosedOn(date: Moment): boolean {
    const notReady = this.vehicleStatus === VehicleStatus.CHECKED_IN && isPresent(this.workStatus) && !READY_WORK_STATUSES.includes(this.workStatus);
    const isToday = this.start?.isSame(date, 'day') ?? false;
    return !this.isStopped && notReady && isToday;
  }

  copyTo(resourceId: PlannerResourceId, start: Moment): PlannerEvent {
    const copy = new PlannerEvent();
    if (this.eventType === PlannerEventType.PLANNED) {
      copy.id = null;
    } else {
      copy.id = this.id;
    }
    copy.resourceId = resourceId;
    copy.dossierId = this.dossierId;
    copy.start = start;
    copy.end = start.clone().add(durationAsMinutes(this.duration || DEFAULT_PLANNER_EVENT_DURATION), 'minutes');
    return copy;
  }

  copy(): PlannerEvent {
    const copy = Object.assign(new PlannerEvent(), omit(this, 'key'));
    if (copy.eventType === PlannerEventType.PLANNED) {
      copy.duration = DEFAULT_PLANNER_EVENT_DURATION;
    }
    copy.start = null;
    copy.end = null;
    return copy;
  }
}
